import React, { useState, useEffect } from 'react';
import { Board, boardApi, BoardMember, boardMemberApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EditPencil, Check, Xmark, Plus, Trash, BoxIso, NavArrowRight, Page, LogOut, ShieldCheck } from 'iconoir-react';
import { PendingInvitations } from './PendingInvitations';
import { AdBanner } from './AdBanner';
import { PremiumUpgradeBanner } from './PremiumUpgradeBanner';
import { InterstitialAd } from './InterstitialAd';
import { useSubscriptionPlan } from '../hooks/useSubscriptionPlan';

// Couleurs de base stockées en base de données
const COLORS = [
  '#5BC279', // Vert
  '#E5CA5C', // Jaune
  '#E5A333', // Orange
  '#E58D85', // Rouge/Rose
  '#B38EE5', // Violet
  '#66A8CC', // Bleu
  '#85CCDB', // Cyan
  '#A8C585', // Vert clair
  '#E591A8', // Rose
  '#8FA1A8', // Gris
];

// Fonction pour convertir une couleur simple en dégradé
const getGradientFromColor = (color: string): string => {
  const gradientMap: Record<string, string> = {
    '#5BC279': 'linear-gradient(135deg, #5BC279 0%, #4FA368 100%)',
    '#E5CA5C': 'linear-gradient(135deg, #E5CA5C 0%, #C2AA4A 100%)',
    '#E5A333': 'linear-gradient(135deg, #E5A333 0%, #C28A2B 100%)',
    '#ea5d51ff': 'linear-gradient(135deg, #f95749 0%, #c64739ff 100%)',
    '#B38EE5': 'linear-gradient(135deg, #B38EE5 0%, #9678C2 100%)',
    '#66A8CC': 'linear-gradient(135deg, #56aeddff 0%, #147ab6ff 100%)',
    '#85CCDB': 'linear-gradient(135deg, #85CCDB 0%, #70ADB9 100%)',
    '#A8C585': 'linear-gradient(135deg, #A8C585 0%, #8DA670 100%)',
    '#E591A8': 'linear-gradient(135deg, #E591A8 0%, #C27A8D 100%)',
    '#8FA1A8': 'linear-gradient(135deg, #8FA1A8 0%, #78898F 100%)',
  };
  return gradientMap[color] || color;
};

export const BoardAdmin: React.FC = () => {
  const { user, logout } = useAuth();
  const { hasAds } = useSubscriptionPlan();
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingBoardId, setEditingBoardId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newBoardTitle, setNewBoardTitle] = useState('');
  const [showColorPalette, setShowColorPalette] = useState<number | null>(null);
  const [showInterstitialAd, setShowInterstitialAd] = useState(false);
  const [pendingBoardId, setPendingBoardId] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadBoards();
  }, []);

  const loadBoards = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await boardApi.getAll();
      // La réponse contient maintenant { boards, has_overages, overages }
      const data: any = response.data;
      setBoards(Array.isArray(data) ? data : data.boards || []);
      
      // Afficher un avertissement si dépassement
      if (data.has_overages) {
        setError('Attention: Vous avez dépassé les limites de votre plan. Certains tableaux peuvent être inaccessibles.');
      }
    } catch (err: any) {
      setError('Erreur lors du chargement des tableaux');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMembers = async (boardId: number) => {
    try {
      const response = await boardMemberApi.getAll(boardId);
      setMembers(response.data);
    } catch (err: any) {
      console.error('Erreur lors du chargement des membres', err);
    }
  };

  const handleSelectBoard = async (board: Board) => {
    setSelectedBoard(board);
    await loadMembers(board.id);
  };

  const handleUpdateColor = async (boardId: number, color: string) => {
    try {
      await boardApi.update(boardId, { background_color: color });
      await loadBoards();
      if (selectedBoard?.id === boardId) {
        setSelectedBoard({ ...selectedBoard, background_color: color });
      }
    } catch (err: any) {
      setError('Erreur lors de la mise à jour de la couleur');
    }
  };

  const handleStartEdit = (board: Board) => {
    setEditingBoardId(board.id);
    setEditTitle(board.title);
  };

  const handleSaveTitle = async (boardId: number) => {
    if (!editTitle.trim()) return;
    
    try {
      await boardApi.update(boardId, { title: editTitle.trim() });
      await loadBoards();
      if (selectedBoard?.id === boardId) {
        setSelectedBoard({ ...selectedBoard, title: editTitle.trim() });
      }
      setEditingBoardId(null);
    } catch (err: any) {
      setError('Erreur lors de la mise à jour du titre');
    }
  };

  const handleCancelEdit = () => {
    setEditingBoardId(null);
    setEditTitle('');
  };

  const handleCreateBoard = async () => {
    if (!newBoardTitle.trim()) return;
    
    setIsCreating(true);
    try {
      await boardApi.create({ title: newBoardTitle.trim() });
      setNewBoardTitle('');
      await loadBoards();
    } catch (err: any) {
      setError('Erreur lors de la création du tableau');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteBoard = async (boardId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce tableau ? Cette action est irréversible.')) {
      return;
    }
    
    try {
      await boardApi.delete(boardId);
      if (selectedBoard?.id === boardId) {
        setSelectedBoard(null);
        setMembers([]);
      }
      await loadBoards();
    } catch (err: any) {
      setError('Erreur lors de la suppression du tableau');
    }
  };

  const handleViewArchived = (boardId: number) => {
    navigate(`/boards/${boardId}/archived`);
  };

  const handleOpenBoard = (boardId: number) => {
    if (hasAds) {
      // Afficher la publicité interstitielle pour les utilisateurs du plan gratuit
      setPendingBoardId(boardId);
      setShowInterstitialAd(true);
    } else {
      // Accès direct pour les utilisateurs premium
      navigate(`/boards/${boardId}`);
    }
  };

  const handleCloseInterstitialAd = () => {
    setShowInterstitialAd(false);
    if (pendingBoardId) {
      navigate(`/boards/${pendingBoardId}`);
      setPendingBoardId(null);
    }
  };

  const getBoardStats = (board: Board) => {
    const totalCards = board.columns.reduce((sum, col) => sum + col.cards.length, 0);
    const activeCards = board.columns.reduce((sum, col) => 
      sum + col.cards.filter(card => !card.archived).length, 0);
    const archivedCards = board.columns.reduce((sum, col) => 
      sum + col.cards.filter(card => card.archived).length, 0);
    
    return { totalCards, activeCards, archivedCards };
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'role-badge role-super-admin';
      case 'admin':
        return 'role-badge role-admin';
      case 'editor':
        return 'role-badge role-editor';
      case 'visitor':
        return 'role-badge role-visitor';
      default:
        return 'role-badge';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Administrateur';
      case 'admin':
        return 'Administrateur';
      case 'editor':
        return 'Éditeur';
      case 'visitor':
        return 'Visiteur';
      default:
        return role;
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Voulez-vous vraiment vous déconnecter ?')) {
      await logout();
      navigate('/login');
    }
  };

  const getRoleBadge = (role: string) => {
    const badges = {
      super_admin: { label: 'Super Administrateur', color: '#9b59b6' },
      admin: { label: 'Administrateur', color: '#e74c3c' },
      editor: { label: 'Éditeur', color: '#3498db' },
      visitor: { label: 'Visiteur', color: '#95a5a6' },
    };
    return badges[role as keyof typeof badges] || badges.visitor;
  };

  const badge = user ? getRoleBadge(user.role) : { label: 'Visiteur', color: '#95a5a6' };

  return (
    <div className="admin-page">
      {/* Navbar */}
      <div className="navbar">
        <div className="navbar-content">
          <div className="navbar-left">
            <h2 className="navbar-brand">Dashboard</h2>
          </div>
          <div className="navbar-right">
            {user && (
              <>
                {user.role === 'super_admin' && (
                  <button
                    className="btn btn-nav"
                    onClick={() => navigate('/admin/saas')}
                    style={{ backgroundColor: '#9b59b6', color: 'white', border: '2px solid #8e44ad' }}
                    title="Interface d'administration SaaS"
                  >
                    <ShieldCheck width={18} height={18} strokeWidth={2} />
                    <span>Admin SaaS</span>
                  </button>
                )}
                <div className="user-info" onClick={() => navigate('/account')} style={{ cursor: 'pointer' }} title="Paramètres du profil">
                  <div className="user-avatar">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} className="user-avatar-image" />
                    ) : (
                      user.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="user-details">
                    <div className="user-name">{user.name}</div>
                    <div className="user-role" style={{ backgroundColor: badge.color }}>
                      {badge.label}
                    </div>
                  </div>
                </div>
                <button className="btn btn-logout" onClick={handleLogout} title="Déconnexion">
                  <LogOut width={18} height={18} strokeWidth={2} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ margin: '20px' }}>
          {error}
        </div>
      )}

      <div className="admin-content">
        {/* Invitations en attente */}
        <PendingInvitations />

        {/* Bannière publicitaire */}
        <AdBanner position="top" />

        {/* Bannière upgrade premium */}
        <PremiumUpgradeBanner />

        {/* En-tête avec formulaire de création */}
        <div className="admin-boards-header">
          <h2>Mes tableaux ({boards.length})</h2>
          <div className="create-board-inline">
            <input
              type="text"
              value={newBoardTitle}
              onChange={(e) => setNewBoardTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateBoard()}
              placeholder="Créer un nouveau tableau..."
              className="create-board-input-inline"
              disabled={isCreating}
            />
            <button
              className="btn btn-primary"
              onClick={handleCreateBoard}
              disabled={isCreating || !newBoardTitle.trim()}
            >
              Créer
            </button>
          </div>
        </div>

        {/* Grille des tableaux */}
        <div className="admin-boards-list">
          {isLoading ? (
            <div className="loading-message">Chargement...</div>
          ) : boards.length === 0 ? (
            <div className="empty-message">Aucun tableau créé</div>
          ) : (
            <div className="boards-grid-responsive">
              {boards.map((board) => {
                const stats = getBoardStats(board);
                const memberCount = board.columns[0]?.cards[0] ? 0 : 0; // Sera calculé côté serveur
                
                return (
                  <div key={board.id} className="board-row">
                    <div 
                      className="board-row-color"
                      style={{ backgroundColor: board.background_color || '#00bf7e' }}
                    />
                    
                    <div className="board-row-content">
                      <div className="board-row-header">
                        {editingBoardId === board.id ? (
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onBlur={() => handleSaveTitle(board.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveTitle(board.id);
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                            autoFocus
                            className="board-row-title-input"
                          />
                        ) : (
                          <h3 className="board-row-title">{board.title}</h3>
                        )}
                        
                        <div className="board-row-actions">
                          <button
                            className="btn-icon"
                            onClick={() => handleStartEdit(board)}
                            title="Renommer"
                          >
                            <EditPencil width={18} height={18} strokeWidth={2} />
                          </button>
                          <button
                            className="btn-color-badge"
                            onClick={() => setShowColorPalette(showColorPalette === board.id ? null : board.id)}
                            title="Changer la couleur"
                            style={{ 
                              background: board.background_color 
                                ? getGradientFromColor(board.background_color) 
                                : '#00bf7e'
                            }}
                          />
                          <button
                            className="btn-icon btn-icon-danger"
                            onClick={() => handleDeleteBoard(board.id)}
                            title="Supprimer"
                          >
                            <Trash width={18} height={18} strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="board-row-stats">
                        <div className="board-stat">
                          <span className="board-stat-label">Cartes actives:</span>
                          <span className="board-stat-value">{stats.activeCards}</span>
                        </div>
                        <div className="board-stat">
                          <span className="board-stat-label">Cartes archivées:</span>
                          <span className="board-stat-value board-stat-archived">{stats.archivedCards}</span>
                        </div>
                        <div className="board-stat">
                          <span className="board-stat-label">Total:</span>
                          <span className="board-stat-value">{stats.totalCards}</span>
                        </div>
                        <div className="board-stat">
                          <span className="board-stat-label">Membres:</span>
                          <span className="board-stat-value">{board.members_count ?? '...'}</span>
                        </div>
                      </div>
                      
                      <div className="board-row-buttons">
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleOpenBoard(board.id)}
                        >
                          <NavArrowRight width={18} height={18} strokeWidth={2} />
                          <span>Ouvrir</span>
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleViewArchived(board.id)}
                          disabled={stats.archivedCards === 0}
                        >
                          <BoxIso width={18} height={18} strokeWidth={2} />
                          <span>Cartes archivées</span>
                        </button>
                      </div>
                      
                      {/* Palette de couleurs */}
                      {showColorPalette === board.id && (
                        <div className="board-row-color-palette">
                          <h4>Choisir une couleur</h4>
                          <div className="color-palette-grid">
                            {/* Option pour réinitialiser à la couleur par défaut */}
                            <button
                              className={`color-option color-option-reset ${!board.background_color ? 'selected' : ''}`}
                              onClick={() => {
                                handleUpdateColor(board.id, '');
                                setShowColorPalette(null);
                              }}
                              title="Couleur par défaut (vert)"
                            >
                              <Xmark width={18} height={18} strokeWidth={2} />
                            </button>
                            {COLORS.map((color) => (
                              <button
                                key={color}
                                className={`color-option ${board.background_color === color ? 'selected' : ''}`}
                                style={{ background: getGradientFromColor(color) }}
                                onClick={() => {
                                  handleUpdateColor(board.id, color);
                                  setShowColorPalette(null);
                                }}
                                title={color}
                              >
                                {board.background_color === color && (
                                  <span className="color-check">
                                    <Check width={18} height={18} strokeWidth={2} />
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Publicité interstitielle pleine page */}
      {showInterstitialAd && (
        <InterstitialAd onClose={handleCloseInterstitialAd} />
      )}
    </div>
  );
};
